// 조직 멤버·초대 관리 (IMP-5)
//
// 개인 작업 공간에서는 이 화면이 "조직 만들기"만 보여준다 — 혼자 쓰는 사람에게
// 멤버·역할 표를 들이밀 이유가 없다(설계 §8).
import { useCallback, useEffect, useState, type FormEvent } from "react";
import {
  activeTenantId,
  tenantApi,
  type ClosurePreflight,
  type InvitationItem,
  type TenantMember,
  type TenantSummary,
  type TransferItem,
} from "../api/tenants.ts";
import { billingApi, type PoolSpaceUsage, type QuotaStatus } from "../api/billing.ts";
import { ApiError, EmailChipsInput, isEmailLike, type EmailChip } from "@taplink/web-shared";
import { useAuth } from "@taplink/web-shared";

const ROLE_LABELS: Record<string, string> = {
  OWNER: "소유자",
  ADMIN: "관리자",
  MEMBER: "멤버",
};

/**
 * 사람이 골라서 줄 수 있는 역할.
 *
 * ⚠️ `OWNER` 가 없다(DSN-19). 소유자는 조직당 정확히 한 명이고
 *    `uq_membership_single_owner` 로 DB 에 못박혀 있다. 되는 길은 **조직 생성**과
 *    **소유권 이전** 둘뿐이므로, 초대·역할 변경 어느 쪽에서도 고를 수 없어야 한다.
 *    `ROLE_LABELS` 를 그대로 돌려 쓰면 '소유자'가 다시 목록에 섞인다.
 */
const ASSIGNABLE_ROLES = ["ADMIN", "MEMBER"] as const;

const ROLE_HELP: Record<string, string> = {
  OWNER: "결제·조직 삭제·소유권 이전까지 가능합니다.",
  ADMIN: "멤버 초대·제거와 모든 링크 관리가 가능합니다.",
  MEMBER: "링크를 만들고 조직 링크를 볼 수 있습니다.",
};

export function TeamPage() {
  const { me, applyToken } = useAuth();
  const [tenants, setTenants] = useState<TenantSummary[]>([]);
  const [members, setMembers] = useState<TenantMember[]>([]);
  const [invitations, setInvitations] = useState<InvitationItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const [orgName, setOrgName] = useState("");
  /** 승격할 때 쓸 조직 이름 — 개인 공간 이름(로그인 아이디)을 그대로 쓰면 안 된다 */
  const [promoteName, setPromoteName] = useState("");
  /** 초대할 주소들. 이메일 **단위**로 다룬다 — 하나가 거절돼도 그 칩에만 이유가 붙는다. */
  const [inviteChips, setInviteChips] = useState<EmailChip[]>([]);
  const [inviteRole, setInviteRole] = useState<TenantMember["role"]>("MEMBER");
  const [inviting, setInviting] = useState(false);
  /**
   * 좌석 한도.
   *
   * ⚠️ 판정 기준은 **"요금제가 FREE 인가"가 아니라 "좌석이 남았는가"** 다.
   *    FREE·LITE 는 1석이라 소유자가 이미 차지해 초대가 구조적으로 불가능하지만,
   *    PLUS(5석)도 5명을 채우면 똑같다. 요금제 이름으로 판정하면 그 경우를 놓치고,
   *    요금제가 늘어날 때마다 이 화면을 다시 고쳐야 한다.
   * null = 아직 못 읽음(그때는 막지 않는다 — 서버가 최종 판정을 한다)
   */
  const [seats, setSeats] = useState<QuotaStatus | null>(null);
  /** 이 조직에서 넘기려고 지목해 둔 요청. null = 없음 */
  const [outgoing, setOutgoing] = useState<TransferItem | null>(null);
  /**
   * 나에게 온 이전 요청 — **작업 공간과 무관하게** 온다.
   * 지목당한 사람이 그 조직으로 전환해 있을 이유가 없으므로 전역으로 묻는다.
   */
  const [incoming, setIncoming] = useState<TransferItem[]>([]);
  /** 접기 전 점검 결과. null = 아직 안 눌렀거나 개인 공간 */
  const [preflight, setPreflight] = useState<ClosurePreflight | null>(null);
  const [renameTo, setRenameTo] = useState("");
  const [closeTarget, setCloseTarget] = useState<string>("");
  /** 초대할 조직. "" = 현재 작업 공간. 소유자만 다른 값을 고를 수 있다 */
  const [inviteTenant, setInviteTenant] = useState<string>("");
  /** 풀 안의 공간별 내역 — 좌석이 부족할 때 어디를 정리할지 보여준다 */
  const [spaces, setSpaces] = useState<PoolSpaceUsage[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const list = await tenantApi.list();
      setTenants(list);
      const [memberList, invites, usage, transfer, mine, spaceList] = await Promise.all([
        tenantApi.members(),
        // 멤버 권한이면 초대 목록은 403이다 — 화면을 막지 않고 비워 둔다
        tenantApi.invitations().catch(() => [] as InvitationItem[]),
        // 좌석 안내용. 실패해도 화면을 막지 않는다 — 안내가 없을 뿐이다
        billingApi.usage().catch(() => null),
        // 이전 요청 조회는 실패해도 화면을 막지 않는다(개인 공간에서는 의미가 없다)
        tenantApi.currentTransfer().catch(() => null),
        tenantApi.incomingTransfers().catch(() => [] as TransferItem[]),
        // 공간별 내역. 실패해도 화면을 막지 않는다 — 총량은 위에서 이미 보인다
        billingApi.spaces().catch(() => [] as PoolSpaceUsage[]),
      ]);
      setMembers(memberList);
      setInvitations(invites);
      setSeats(usage?.quotas.find((q) => q.feature === "SEATS") ?? null);
      setOutgoing(transfer ?? null);
      setIncoming(mine);
      setSpaces(spaceList);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const myRole = members.find((m) => m.loginId === me?.loginId)?.role;
  const canManage = myRole === "OWNER" || myRole === "ADMIN";
  /**
   * 남은 좌석. null = 무제한이거나 아직 못 읽음 — 둘 다 **막지 않는다.**
   * 서버가 최종 판정을 하므로 화면이 앞질러 잠그면 무제한 요금제에서 잘못 막힌다.
   */
  /** 내가 소유한 조직들 — 초대 폼의 조직 선택에 쓴다 */
  const ownedOrgs = tenants.filter((t) => t.type === "ORGANIZATION" && t.role === "OWNER");
  /**
   * 지금 보고 있는 공간. 초대가 **어디로 가는지**를 화면에 이름으로 적기 위해 필요하다.
   *
   * 예전에는 알 방법이 없어 '현재 작업 공간'이라고만 적었고, 그것이 개인 공간이어도
   * 드러나지 않았다 — 실측(운영 2026-08-24) 개인 공간에 사람 둘이 초대됐다.
   */
  /**
   * 승격 경고에 적을 링크 수 — `spaces` 선언 뒤여야 한다.
   * (처음에는 상태 선언부에 뒀다가 `spaces` 를 선언 전에 읽어 타입 검사가 잡아냈다.)
   */
  const myLinkCount = spaces.find((sp) => sp.type === "PERSONAL")?.activeLinks ?? null;
  const currentSpace = tenants.find((t) => t.id === activeTenantId(tenants));
  const inPersonalSpace = currentSpace?.type === "PERSONAL";

  // 개인 공간에서 초대 폼을 열면 대상을 **조직으로 미리 정해 둔다.** 비워 두면 서버가
  // 거절하는 값(개인 공간)이 기본이 되어, 눌러 보고 알게 된다.
  useEffect(() => {
    if (inPersonalSpace && !inviteTenant && ownedOrgs.length > 0) {
      setInviteTenant(String(ownedOrgs[0]!.id));
    }
  }, [inPersonalSpace, inviteTenant, ownedOrgs]);
  const seatsLeft = seats == null || seats.limit == null ? null : seats.remaining ?? null;
  const seatsFull = seatsLeft !== null && seatsLeft <= 0;
  const readyCount = inviteChips.filter((c) => isEmailLike(c.value)).length;
  /** 넣은 수가 남은 좌석보다 많은가 — 보내기 전에 알려 준다 */
  const overSeats = seatsLeft !== null && readyCount > seatsLeft;
  const isPersonal = members.length === 1 && !invitations.length && tenants.length > 0 &&
    tenants.every((t) => t.type === "PERSONAL");

  const createOrg = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      const created = await tenantApi.create(orgName.trim());
      const token = await tenantApi.switch(created.id);
      await applyToken(token.accessToken);
      // 새 조직으로 옮겨간 상태에서 화면 전체를 다시 읽는다
      window.location.reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "조직을 만들지 못했습니다.");
    }
  };

  /**
   * 여러 명 초대.
   *
   * ⚠️ **한 건씩 순서대로** 보낸다. 병렬로 보내면 좌석 예약이 서로 경쟁해 한도를 넘겨
   *    예약될 수 있다(쿼터는 "증가 후 검사" 방식이다).
   * ⚠️ 전부-또는-전무로 묶지 않는다. 좌석이 1개 남았는데 5명을 넣었다면 묶어서 실패시키는
   *    것보다 1명이라도 초대되는 편이 낫다. 대신 **어느 주소가 왜 안 됐는지**를 남긴다.
   * 성공한 주소는 칩에서 빠지고, 실패한 주소는 이유를 달고 남아 고칠 수 있다.
   */
  const invite = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setNotice(null);

    const targets = inviteChips.filter((c) => isEmailLike(c.value));
    if (targets.length === 0) return;

    setInviting(true);
    const failed: EmailChip[] = [];
    let sent = 0;
    try {
      for (const chip of targets) {
        try {
          await tenantApi.invite(chip.value, inviteRole, inviteTenant ? Number(inviteTenant) : undefined);
          sent += 1;
        } catch (e) {
          failed.push({ value: chip.value, error: e instanceof ApiError ? e.message : "초대하지 못했습니다." });
        }
      }
    } finally {
      setInviting(false);
    }

    // 형식이 잘못돼 보내지 않은 것도 남긴다 — 조용히 사라지면 보낸 줄 안다
    const skipped = inviteChips.filter((c) => !isEmailLike(c.value));
    setInviteChips([...failed, ...skipped]);

    // ⚠️ 목록을 **먼저** 다시 읽는다. load() 는 시작할 때 setError(null) 을 하므로
    //    결과 문구를 먼저 넣으면 그대로 지워진다(실측: 좌석 초과 3건이 조용히 사라졌다).
    await load();

    // 보내지 못한 주소는 아래 실패 목록에 이유가 붙는다(본인·이미 멤버·이미 초대됨).
    // 예전에는 서버가 그 경우도 성공을 돌려줘 **안 보냈는데 보냈다고** 말했다.
    if (sent > 0) setNotice(`초대 메일을 ${sent}명에게 발송했습니다.`);
    if (failed.length > 0) {
      setError(
        `${failed.length}명은 초대하지 못했습니다 — ${failed
          .map((f) => `${f.value}: ${f.error}`)
          .join(" / ")}`,
      );
    }
  };

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setError(null);
    try {
      await fn();
      setNotice(done);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "처리하지 못했습니다.");
    }
  };

  if (loading) return <p role="status">불러오는 중…</p>;

  return (
    <>
      {error && <div className="alert alert-error" role="alert">{error}</div>}
      {notice && <div className="alert alert-success" role="status">{notice}</div>}

      {/* 나에게 온 소유권 이전 요청.
          ⚠️ **가장 위에** 둔다. 이 요청은 7일 뒤 조용히 만료되고, 놓치면 그 조직에
             소유자가 그대로 묶인다. 작업 공간을 전환하지 않아도 보이는 것이 요점이다
             — 서버가 작업 공간과 무관하게 돌려준다(RLS 정책의 to_account_id 절, V26). */}
      {incoming.map((t) => (
        <div key={t.id} className="alert alert-warn" role="alert">
          <p>
            <strong>{t.fromLoginId}</strong> 님이 <strong>{t.tenantName}</strong> 의 소유권을
            넘기려 합니다.
          </p>
          <p className="hint">
            승인하면 회원님이 소유자가 되고, 넘긴 분은 회원님의 현재 역할을 받습니다.
            이 조직의 좌석은 회원님 요금제에서 나가므로 한도를 넘으면 승인이 거절됩니다.{" "}
            {new Date(t.expiresAt).toLocaleDateString("ko-KR")} 까지 유효합니다.
          </p>
          <div className="row-actions">
            <button
              type="button"
              className="btn-success"
              onClick={() =>
                void act(async () => {
                  await tenantApi.acceptTransfer(t.id);
                  // ⚠️ 재로그인 안내가 필요하다. 토큰에 역할이 박혀 있어(tenantRole 클레임)
                  //    승인 직후에도 서버는 옛 역할로 판정한다 — 남의 링크 관리
                  //    (LinkService)와 조직 활동 기록(TenantAuditController)이 그 클레임을
                  //    보므로, 새 소유자가 그 둘을 못 한다.
                  window.alert(
                    "소유권을 받았습니다.\n반영을 위해 로그아웃 후 다시 로그인해 주세요 — " +
                      "그때까지 남의 링크 관리와 조직 활동 기록은 이전 권한으로 동작합니다.",
                  );
                }, "소유권을 받았습니다.")
              }
            >
              승인
            </button>
            <button
              type="button"
              className="btn-outline"
              onClick={() => void act(() => tenantApi.declineTransfer(t.id), "요청을 거절했습니다.")}
            >
              거절
            </button>
          </div>
        </div>
      ))}

      {/* 내가 지목해 둔 요청 — 상대가 승인하기 전까지 그대로 있다 */}
      {outgoing && (
        <div className="alert alert-info" role="status">
          <p>
            <strong>{outgoing.toLoginId}</strong> 님에게 소유권 이전을 요청했습니다 — 승인
            대기 중({new Date(outgoing.expiresAt).toLocaleDateString("ko-KR")} 까지).
          </p>
          {myRole === "OWNER" && (
            <button
              type="button"
              className="btn-outline"
              onClick={() => void act(() => tenantApi.cancelTransfer(), "요청을 거둬들였습니다.")}
            >
              요청 취소
            </button>
          )}
        </div>
      )}

      {isPersonal && (
        <section className="panel">
          <h2>팀과 함께 쓰기</h2>
          <p className="subtitle">
            두 가지 길이 있습니다. <strong>빈 조직을 만들면</strong> 지금 링크는 개인
            공간에 그대로 남고 새 공간에서 시작합니다. <strong>이 공간을 조직으로
            바꾸면</strong> 지금 링크가 그대로 조직의 것이 됩니다 — 아래를 보세요.
          </p>
          <p className="hint">
            어느 쪽이든 한도는 계정 단위로 함께 씁니다 — 조직을 만들어도 좌석이 따로
            늘지 않습니다.
          </p>
          <form onSubmit={createOrg}>
            <div className="field">
              <label htmlFor="org-name">조직 이름</label>
              <input
                id="org-name"
                required
                maxLength={100}
                placeholder="예) 우리회사 마케팅팀"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
              />
            </div>
            <button type="submit" className="btn-primary btn-block" disabled={!orgName.trim()}>
              빈 조직 만들기
            </button>
          </form>

          {/* 승격 — 같은 화면에 둔다. 두 길의 **차이가 곧 결정 기준**이라 나란히 놓고
              무엇이 다른지 적어야 고를 수 있다. 따로 두면 하나만 보고 고른다. */}
          <div className="danger-zone">
            <h3>또는, 이 공간을 조직으로</h3>
            <p className="hint">
              지금 쓰는 개인 작업 공간을 그대로 조직으로 바꿉니다.{" "}
              <strong>링크를 옮기지 않으므로 단축 주소가 그대로 살아 있습니다</strong> —
              명함·전단에 적힌 주소가 죽지 않습니다. 통계와 QR 도 따라옵니다.
            </p>
            {/* ⚠️ 되돌릴 수 없다는 것과 **무엇이 보이게 되는지**를 숫자로 말한다.
                "조직이 됩니다" 만으로는 내 링크가 남에게 보인다는 뜻이 전달되지 않는다. */}
            <div className="alert alert-warn" role="status">
              바꾼 뒤 사람을 초대하면{" "}
              {myLinkCount === null ? "지금까지 만든 링크가" : <strong>지금 링크 {myLinkCount}개가</strong>}{" "}
              그 사람들에게 보입니다. <strong>되돌릴 수 없습니다.</strong>
              {" "}개인 링크를 남겨 두고 싶다면 위에서 <strong>빈 조직</strong>을 만드세요.
            </div>
            <div className="field">
              <label htmlFor="promote-name">조직 이름</label>
              <input
                id="promote-name"
                maxLength={100}
                placeholder="예) 우리회사 마케팅팀"
                value={promoteName}
                onChange={(e) => setPromoteName(e.target.value)}
              />
              {/* 개인 공간 이름은 대개 로그인 아이디다 — 그대로 두면 팀원 화면에
                  남의 메일 주소가 뜬다. 그래서 서버도 이름을 필수로 받는다. */}
              <p className="hint">개인 공간 이름(로그인 아이디)은 팀원에게 보이므로 새로 정합니다.</p>
            </div>
            <button
              type="button"
              className="btn-danger"
              disabled={!promoteName.trim()}
              onClick={() => {
                const ok = window.confirm(
                  "이 개인 작업 공간을 조직 '" + promoteName.trim() + "' 으로 바꿀까요?\n\n" +
                    (myLinkCount === null ? "" : "지금 링크 " + myLinkCount + "개가 조직의 것이 됩니다.\n") +
                    "초대한 사람들이 그 링크를 보게 되고, 되돌릴 수 없습니다.\n" +
                    "새 개인 작업 공간이 하나 만들어집니다.",
                );
                if (!ok) return;
                void act(async () => {
                  await tenantApi.promote(promoteName.trim());
                  setPromoteName("");
                  // 같은 공간이 조직이 됐다 — 토큰의 tenantId 는 그대로지만 종류·이름이
                  // 바뀌었고 새 개인 공간도 생겼다. 화면을 새로 띄워 목록을 다시 읽는다.
                  window.location.reload();
                }, "이 공간을 조직으로 바꿨습니다.");
              }}
            >
              이 공간을 조직으로 바꾸기
            </button>
          </div>
        </section>
      )}

      <section className="panel">
        <h2>멤버</h2>
        <p className="subtitle">
          {isPersonal
            ? "개인 작업 공간입니다. 조직을 만들면 멤버를 초대할 수 있습니다."
            : "조직의 링크와 한도를 함께 사용하는 사람들입니다."}
        </p>

        <div className="table-scroll">
          <table className="link-table">
            <caption className="sr-only">작업 공간 멤버 목록</caption>
            <thead>
              <tr>
                <th scope="col">계정</th>
                <th scope="col">역할</th>
                <th scope="col">합류</th>
                {/* 개인 작업 공간에서는 역할 변경·제거가 의미가 없다 — 서버도 항상 거부한다
                    (CANNOT_LEAVE_PERSONAL). 눌러도 실패하는 버튼을 보여주지 않는다. */}
                {canManage && !isPersonal && <th scope="col">작업</th>}
              </tr>
            </thead>
            <tbody>
              {members.map((member) => (
                <tr key={member.accountId}>
                  <td className="truncate" title={member.loginId}>
                    {member.displayName || member.loginId}
                  </td>
                  <td>
                    <span className="badge">{ROLE_LABELS[member.role] ?? member.role}</span>
                  </td>
                  <td className="hint">{new Date(member.joinedAt).toLocaleDateString("ko-KR")}</td>
                  {canManage && !isPersonal && (
                    <td>
                      {/* ⚠️ 소유자 행에는 조작 요소를 두지 않는다(DSN-19).
                          소유자는 조직당 정확히 한 명이고(`uq_membership_single_owner`)
                          바뀌는 길은 **조직 생성**과 **소유권 이전** 둘뿐이다. 그래서
                          아래 드롭다운은 '소유자'를 내보내지 않는데, 소유자 행에 그 드롭다운을
                          두면 현재 값이 목록에 없어 **빈 칸**으로 보인다. 제거 쪽은 서버가
                          `LAST_OWNER` 로 거절한다. 둘 다 **눌리는데 되지 않는** 조작 요소다.

                          예전에는 `Object.keys(ROLE_LABELS)` 로 세 역할을 그대로 내보냈다.
                          초대 폼(아래)은 역할에 따라 걸러내는데 여기는 걸러내지 않아서,
                          관리자가 '소유자'를 고를 수 있는데 서버가 거절하는 상태였다.

                          소유권 이전이 붙으면 이 자리에 '소유권 넘기기'가 들어간다. */}
                      {member.role === "OWNER" ? (
                        <span className="hint">소유권 이전으로만 바뀝니다</span>
                      ) : (
                        <div className="row-actions">
                          <label className="sr-only" htmlFor={`role-${member.accountId}`}>
                            {member.loginId} 역할
                          </label>
                          <select
                            id={`role-${member.accountId}`}
                            value={member.role}
                            onChange={(e) =>
                              void act(
                                () => tenantApi.changeRole(member.accountId, e.target.value as TenantMember["role"]),
                                "역할을 변경했습니다.",
                              )
                            }
                          >
                            {ASSIGNABLE_ROLES.map((role) => (
                              <option key={role} value={role}>
                                {ROLE_LABELS[role]}
                              </option>
                            ))}
                          </select>
                          {/* 소유권 넘기기는 **소유자에게만** 보인다. 지목은 요청일 뿐이고
                              받는 사람이 승인해야 바뀐다 — 그래서 문구도 '넘기기'가 아니라
                              요청임을 드러낸다. */}
                          {myRole === "OWNER" && (
                            <button
                              type="button"
                              className="btn-outline"
                              onClick={() => {
                                const ok = window.confirm(
                                  member.loginId + " 님에게 소유권 이전을 요청할까요?\n" +
                                  "상대가 승인하면 두 분의 역할이 맞바뀝니다(회원님은 " +
                                  ROLE_LABELS[member.role] + "가 됩니다).",
                                );
                                if (!ok) return;
                                void act(() => tenantApi.nominateOwner(member.accountId), "소유권 이전을 요청했습니다.");
                              }}
                            >
                              소유권 넘기기
                            </button>
                          )}
                          <button
                            type="button"
                            className="btn-outline-danger"
                            onClick={() => {
                              if (!window.confirm(`${member.loginId} 님을 제거할까요?\n이 사람이 만든 링크는 조직에 남습니다.`)) return;
                              void act(() => tenantApi.removeMember(member.accountId), "멤버를 제거했습니다.");
                            }}
                          >
                            제거
                          </button>
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {canManage && !isPersonal && (
          <p className="hint">
            역할별 권한 — {Object.entries(ROLE_HELP).map(([role, help]) => `${ROLE_LABELS[role]}: ${help}`).join(" ")}
          </p>
        )}
      </section>

      {/* 풀 사용 현황 — 한도는 계정이 갖고 공간들이 나눠 쓴다(DSN-19 §8).
          공간이 둘 이상일 때만 보여준다: 하나뿐이면 위의 총량과 같은 값이라 줄만 늘어난다. */}
      {spaces.length > 1 && (
        <section className="panel">
          <h2>작업 공간별 사용</h2>
          <p className="subtitle">
            좌석·링크 한도는 내가 소유한 모든 작업 공간이 함께 씁니다. 한도가 모자랄 때
            어디를 정리할지 여기서 봅니다.
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>작업 공간</th>
                  <th>사람</th>
                  <th>대기 초대</th>
                  <th>활성 링크</th>
                  <th>이번 달 생성</th>
                </tr>
              </thead>
              <tbody>
                {spaces.map((sp) => (
                  <tr key={sp.tenantId}>
                    <td>
                      {sp.name}
                      {sp.type === "PERSONAL" && <span className="badge"> 개인</span>}
                    </td>
                    <td>{sp.seats}</td>
                    <td>{sp.pendingInvitations}</td>
                    <td>{sp.activeLinks}</td>
                    <td>{sp.linksThisMonth}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* ⚠️ 이 설명을 빼면 "숫자가 안 맞는다"로 읽힌다. 풀은 사람을 중복 없이 세고
              이 표는 공간별로 세므로, 같은 사람이 두 조직에 있으면 합이 커진다. */}
          <p className="hint">
            같은 사람이 두 작업 공간에 있으면 위 표에는 각각 한 명으로 잡히지만
            좌석은 <strong>1석</strong>만 씁니다 — 그래서 합이 팀 좌석 수보다 클 수 있습니다.
          </p>
        </section>
      )}

      {/* 조직 설정 — 이름 변경(OWNER·ADMIN)과 접기(OWNER) */}
      {canManage && !isPersonal && (
        <section className="panel">
          <h2>조직 설정</h2>

          <form
            onSubmit={(e) => {
              e.preventDefault();
              const next = renameTo.trim();
              if (!next) return;
              void act(async () => {
                await tenantApi.rename(next);
                setRenameTo("");
              }, "조직 이름을 바꿨습니다.");
            }}
          >
            <div className="field">
              <label htmlFor="rename-to">조직 이름</label>
              <input
                id="rename-to"
                maxLength={100}
                placeholder={tenants.find((t) => t.type === "ORGANIZATION")?.name ?? "새 이름"}
                value={renameTo}
                onChange={(e) => setRenameTo(e.target.value)}
              />
              {/* 대기 초대가 있어도 막지 않는다(§5.4) — 수락 화면이 현재 이름을 보여주므로
                  이름 변경은 스스로 교정된다. 막으면 오타를 7일간 고칠 수 없다. */}
              <p className="hint">합류를 기다리는 사람에게도 새 이름으로 보입니다.</p>
            </div>
            <button type="submit" className="btn-primary" disabled={!renameTo.trim()}>
              이름 바꾸기
            </button>
          </form>

          {myRole === "OWNER" && (
            <div className="danger-zone">
              <h3>조직 접기</h3>
              <p className="hint">
                조직원·링크·사용량을 다른 조직으로 옮기고 이 조직을 닫습니다. 링크는 그대로
                살아 있어 명함·전단에 적힌 주소가 죽지 않습니다. 활동 기록은 이 조직에 남습니다.
              </p>

              {!preflight ? (
                <button
                  type="button"
                  className="btn-outline-danger"
                  onClick={() =>
                    void act(async () => {
                      setPreflight(await tenantApi.closurePreflight());
                    }, "접기 전 점검을 마쳤습니다.")
                  }
                >
                  접기 준비 확인
                </button>
              ) : (
                <>
                  <p>
                    조직원 <strong>{preflight.memberCount}</strong>명 · 링크{" "}
                    <strong>{preflight.linkCount}</strong>개
                    {preflight.pendingInvitations > 0 && (
                      <> · 대기 중인 초대 <strong>{preflight.pendingInvitations}</strong>건</>
                    )}
                  </p>

                  {/* ⚠️ 대기 초대가 있으면 **여기서 막는다.** 눌러 보고 409 로 알게 되면 안 된다.
                      조용히 취소하지도 않는다 — 초대 메일에 이 조직 이름이 적혀 있어서,
                      취소 없이 접으면 메일에 적힌 것과 다른 조직으로 사람이 들어간다. */}
                  {preflight.pendingInvitations > 0 ? (
                    <div className="alert alert-warn" role="alert">
                      대기 중인 초대 {preflight.pendingInvitations}건을 먼저 취소해 주세요 — 위
                      &lsquo;대기 중인 초대&rsquo; 표에서 취소할 수 있습니다. 초대 메일에는 이
                      조직 이름이 적혀 있어, 취소하지 않고 접으면 메일과 다른 조직으로 합류하게
                      됩니다.
                    </div>
                  ) : (
                    <>
                      {preflight.targetRequired && preflight.candidates.length === 0 ? (
                        <div className="alert alert-warn" role="alert">
                          조직원을 옮길 다른 조직이 없어 접을 수 없습니다. 조직을 하나 만든 뒤
                          다시 시도해 주세요.
                        </div>
                      ) : (
                        <>
                          <div className="field">
                            <label htmlFor="close-target">
                              옮길 조직{preflight.targetRequired ? "" : " (선택)"}
                            </label>
                            <select
                              id="close-target"
                              value={closeTarget}
                              onChange={(e) => setCloseTarget(e.target.value)}
                            >
                              <option value="">
                                {preflight.targetRequired ? "선택해 주세요" : "옮기지 않음"}
                              </option>
                              {preflight.candidates.map((c) => (
                                <option key={c.id} value={String(c.id)}>{c.name}</option>
                              ))}
                            </select>
                            <p className="hint">
                              옮겨간 사람은 모두 <strong>멤버</strong>가 됩니다. 개인 작업 공간은
                              고를 수 없습니다.
                            </p>
                          </div>
                          <button
                            type="button"
                            className="btn-danger"
                            disabled={preflight.targetRequired && !closeTarget}
                            onClick={() => {
                              const label = preflight.candidates.find((c) => String(c.id) === closeTarget)?.name;
                              const ok = window.confirm(
                                "이 조직을 접을까요?\n" +
                                  (label ? `조직원 ${preflight.memberCount}명과 링크 ${preflight.linkCount}개가 ${label} 로 옮겨갑니다.` : "옮기지 않고 닫습니다."),
                              );
                              if (!ok) return;
                              void act(async () => {
                                await tenantApi.closeTenant(closeTarget ? Number(closeTarget) : null);
                                // 접은 조직의 토큰으로는 아무것도 할 수 없다(403 TENANT_CLOSED).
                                // 목록을 다시 읽는 것으로는 부족해 화면을 새로 띄운다.
                                window.location.href = "/";
                              }, "조직을 접었습니다.");
                            }}
                          >
                            조직 접기
                          </button>
                        </>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </section>
      )}

      {canManage && !isPersonal && (
        <section className="panel">
          <h2>초대</h2>

          {/* ⚠️ 좌석이 없으면 **폼을 아예 보여주지 않는다.** 예전에는 입력하고 보낸 뒤에야
              429 로 거절됐다 — FREE·LITE 는 1석이라 소유자가 이미 차지해서 **어떤 초대도
              성공할 수 없는데** 그 사실을 시도한 뒤에 알게 됐다.
              대신 무엇을 하면 되는지를 적는다. 아래 '대기 중인 초대' 표는 그대로 두므로
              여기서 바로 취소해 좌석을 비울 수 있다. */}
          {seatsFull ? (
            <div className="alert alert-info" role="status">
              <strong>좌석을 모두 사용했습니다({seats?.used ?? 0}/{seats?.limit ?? 0}석).</strong>{" "}
              지금은 초대할 수 없습니다. 수락 대기 중인 초대도 좌석을 차지하므로, 아래에서{" "}
              <strong>대기 중인 초대를 취소</strong>하거나 요금제를 올리면 초대할 수 있습니다.
            </div>
          ) : (
          <form onSubmit={invite}>
            <EmailChipsInput
              label="이메일"
              hint={
                "Enter 또는 쉼표로 여러 명을 넣습니다. 쉼표로 구분된 목록을 붙여넣어도 되고, 칩의 ✎ 로 하나씩 고칠 수 있습니다." +
                // 남은 좌석은 **먼저** 알려 준다. 여섯 명을 넣고 보낸 뒤에 "두 명은 안 됩니다"를
                // 듣는 것보다, 넣기 전에 몇 자리인지 아는 편이 낫다.
                (seatsLeft !== null ? ` 남은 좌석 ${seatsLeft}석입니다.` : "")
              }
              chips={inviteChips}
              onChange={setInviteChips}
              disabled={inviting}
            />

            {/* 보내기 전에 알려 주되 **막지는 않는다** — 그 사이 좌석이 빌 수도 있고
                최종 판정은 서버가 한다. */}
            {overSeats && (
              <p className="hint email-chips__notice" role="status">
                {readyCount}명을 넣었지만 남은 좌석은 {seatsLeft}석입니다 — 앞의 {seatsLeft}명만
                초대되고 나머지는 거절됩니다.
              </p>
            )}
            <div className="field">
              <label htmlFor="invite-role">역할</label>
              {/* 소유자는 여러 조직을 가질 수 있다. 예전에는 초대가 **현재 작업 공간으로만**
                  갔고, 다른 조직에 초대하려면 전환하고 돌아와야 했다(DSN-19 §3.4).
                  관리자에게는 보이지 않는다 — 위임은 그 조직 안에서만 유효하다. */}
              {/* ⚠️ 조건이 `> 1` 이었다 — 소유 조직이 2개 이상일 때만 보였다. 조직이 하나인
                  사람에게는 선택이 뜨지 않아 **개인 공간에 있는 채로 초대가 나갔다**.
                  고를 것이 하나라도 있으면 보인다. */}
              {myRole === "OWNER" && ownedOrgs.length >= 1 && (
                <>
                  <label htmlFor="invite-tenant">보낼 조직</label>
                  <select
                    id="invite-tenant"
                    value={inviteTenant}
                    onChange={(e) => setInviteTenant(e.target.value)}
                  >
                    {/* 현재 공간을 **이름으로** 적는다. '현재 작업 공간'이라고만 쓰면
                        그것이 개인 공간일 때 드러나지 않는다. 개인 공간이면 선택지에서
                        빼고 조직을 고르게 한다 — 서버도 거절하지만, 고를 수 있는데
                        안 되는 선택지를 두지 않는다. */}
                    {!inPersonalSpace && currentSpace && (
                      <option value="">{currentSpace.name} (지금 보는 곳)</option>
                    )}
                    {ownedOrgs.map((t) => (
                      <option key={t.id} value={String(t.id)}>{t.name}</option>
                    ))}
                  </select>
                </>
              )}
              <select
                id="invite-role"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as TenantMember["role"])}
              >
                {/* 소유자로는 아무도 초대할 수 없다(DSN-19) — 서버도 같은 규칙을 강제한다.
                    예전에는 소유자가 소유자를 초대할 수 있었고, 그러면 한 조직에 소유자가
                    둘이 되어 좌석 풀의 주인이 정해지지 않는다. */}
                {ASSIGNABLE_ROLES.map((role) => (
                  <option key={role} value={role}>
                    {ROLE_LABELS[role]}
                  </option>
                ))}
              </select>
            </div>
            <button
              type="submit"
              className="btn-primary btn-block"
              /* 형식이 맞는 주소가 하나라도 있어야 보낸다 — 잘못된 칩만 남은 상태로 누르면
                 아무 일도 안 일어나 고장으로 읽힌다. */
              disabled={inviting || inviteChips.filter((c) => isEmailLike(c.value)).length === 0}
            >
              {inviting
                ? "초대 메일 보내는 중…"
                : (() => {
                    const n = inviteChips.filter((c) => isEmailLike(c.value)).length;
                    return n > 1 ? `초대 메일 보내기 (${n}명)` : "초대 메일 보내기";
                  })()}
            </button>
          </form>
          )}

          {invitations.length > 0 && (
            <>
              <h3>대기 중인 초대</h3>
              <div className="table-scroll">
                <table className="link-table">
                  <caption className="sr-only">대기 중인 초대 목록</caption>
                  <thead>
                    <tr>
                      <th scope="col">이메일</th>
                      <th scope="col">역할</th>
                      <th scope="col">만료</th>
                      <th scope="col">작업</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((invitation) => (
                      <tr key={invitation.id}>
                        {/* 서버가 마스킹해서 준다 — 목록에 원문 주소를 뿌리지 않는다 */}
                        <td>{invitation.maskedEmail}</td>
                        <td>{ROLE_LABELS[invitation.role] ?? invitation.role}</td>
                        <td className="hint">{new Date(invitation.expiresAt).toLocaleDateString("ko-KR")}</td>
                        <td>
                          <button
                            type="button"
                            className="btn-secondary"
                            onClick={() => {
                              // ⚠️ 확인 없이 취소되고 있었다. 취소하면 **이미 보낸 메일의
                              //    링크가 죽는다** — 상대가 누르면 "만료되었거나 이미
                              //    사용된 초대"가 뜬다. 되돌리려면 새로 초대해야 하고
                              //    메일이 한 통 더 나간다(재발송은 막아 두었으므로
                              //    취소가 곧 되돌릴 수 없는 조작이다).
                              const ok = window.confirm(
                                invitation.maskedEmail + " 님의 초대를 취소할까요?\n" +
                                  "이미 보낸 메일의 링크가 더 이상 동작하지 않습니다. " +
                                  "다시 부르려면 초대를 새로 보내야 합니다.",
                              );
                              if (!ok) return;
                              void act(() => tenantApi.revokeInvitation(invitation.id), "초대를 취소했습니다.");
                            }}
                          >
                            취소
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
      )}
    </>
  );
}
